/** @module csi/siteDefaultLocale */
define([
	"dojo/_base/lang",
	"underscore",
	"csi",
	"csi/comm",
	"csi/config/LocaleResolver",
	"csi/config/LocaleResources",
	"csi/locale",
	"csi/localeLoader",
	"csi/reflection",
	"csi/state/rootStore",
	"csi/xml"
], function(lang, _, csi, comm, LocaleResolver, LocaleResources, locale, {initBaseResolver}, reflection, {localStore, sessionStore}, xml) {

	// LocaleResolver: base locale resolver configured with config resolver to resolve parameters
	var _baseConfigResolver;

	// LocaleResolver: base locale resolver (with OOTB resources)
	var _baseResolvers = {};

	// Object: mapping of customer configured resource resolvers by locale
	var _configResolvers = {};

	// Deferred: deferred used to initialize module
	var _dfdInit;

	// Object: reflection class alias to reflection class map
	var _mapClassBOs;

	// Object[]: list of pending changes to client resources
	var _resourceChanges = [];

	// String: site locale when first loaded - don't want it to change if user changes site locale setting
	var _siteLocale;

	/**
	 * The site locale is not known until the session is initialized, so only access through function rather than the above property directly
	 * @return {string}
	 */
	function _getSiteLocale() {
		_siteLocale = _siteLocale || sessionStore.siteLocale;
		return _siteLocale;
	}

	function _initBaseResolver(/*String*/loc) {
		return initBaseResolver(loc).then(function(lr) {
			_baseResolvers[loc] = lr;
			return lr;
		});
	}

	async function _initConfigResolver(/*String*/loc) {
		// summary: get configuration resolver for given locale - does not include base resources
		let lr = _configResolvers[loc];
		if (lr) {
			return lr;
		}

		const url = sessionStore.siteCompany.LocaleConfiguration[loc];
		const resources = new LocaleResources(loc);
		let node;

		if (url) {
			const response = await comm.getNodeRequest(url).go();
			node = response.getResultNode();
			resources.initFromNode(node);
		}

		lr = _configResolvers[loc] = new LocaleResolver(resources, {
			bSubstitute: false,
			bUseDefault: false
		});

		return lr;
	}

	function loadEditResolvers() {
		// summary: load resolvers needed for editing localized resources
		const editLocale = localStore.editLocale;

		return Promise.all([
			_initBaseResolver(editLocale),
			_initConfigResolver(editLocale)
		]);
	}

	function initialize() {
		// summary: return deferred for initializing resolvers
		if (!_dfdInit) {
			var siteLocale = _getSiteLocale();

			_dfdInit = _initConfigResolver(siteLocale)
				.then(function(configResolver) {
					return initBaseResolver(siteLocale, {paramResolver: configResolver});
				}).then(function(baseResolver) {
					_baseConfigResolver = baseResolver;
				}).then(function() {
					_initBaseResolver(siteLocale);
				}).then(function() {
					return loadEditResolvers();
				});
		}

		return _dfdInit;
	}

	var _Locale = {
		// LocaleConfiguration attributes
		ATTR_RESOURCES: "Resources",
		ATTR_TYPES: "Types",
		ATTR_VIEWS: "Views",

		initialize,
		loadEditResolvers,

		getBaseConfigResolver: function() {
			// summary: get base resolver for site locale to be used in BO views
			console.assert(!!_baseConfigResolver);
			return _baseConfigResolver;
		},

		getBaseResolver: function(/*String?*/loc) {
			// summary: get base resolver for given locale
			var lr = _baseResolvers[loc || _getSiteLocale()];
			console.assert(!!lr);
			return lr;
		},

		getConfigResolver: function(/*String?*/loc) {
			// summary: get configuration resolver for given locale - does not include base resources
			var lr = _configResolvers[loc || _getSiteLocale()];
			console.assert(!!lr);
			return lr;
		},

		getConfigurableAttributeType: function(/*Node*/nodeCA) {
			// summary: return class containing given ConfigurableAttribute
			if (nodeCA.ReflectionAttributeOwner) {
				return nodeCA.ReflectionAttributeOwner;
			}

			// create mapping from BO URL to reflection attribute class type if first time - any attributes
			// that do not have reflection attribute must be on BO's attribute class; assumption is that
			// attribute class is same as reflection class unless it is a master class in which case the attribute class
			// is the revision class
			var obj = _mapClassBOs;
			if (!obj) {
				var url;

				obj = _mapClassBOs = {};

				_.each(reflection.getClasses(), function(rc) {
					if (url = rc.getBusinessObject()) {
						obj[url] = rc.isMaster() ? rc.getRevisionClass().getType() : rc.getType();
					}
				});
			}

			return obj[nodeCA.__Parent__];
		},

		getAttributeIdFromConfigurableAttributeId: function(/*String*/id) {
			// summary: return id of ReflectionAttribute given id of ConfigurableAttribute; make sure to remove class prefix if any
			var index = id.indexOf("/");
			if (index >= 0) {
				id = id.substring(index + 1);
			}

			return id;
		},

		getConfigurableAttributeId: function(/*Node*/nodeCA) {
			// summary: return id of given ConfigurableAttribute; make sure to remove class prefix if any
			var id = nodeCA.$Name;
			return this.getAttributeIdFromConfigurableAttributeId(id);
		},

		getConfigurableAttributeNodeName: function(/*Node*/nodeCA) {
			// summary: return name of given ConfigurableAttribute; first try resolving using LocaleConfiguration
			// for current locale - if not configured, then use cached resources
			return this.getTypeAttribute(this.getConfigurableAttributeType(nodeCA), this.getConfigurableAttributeId(nodeCA));
		},

		getResource: function(/*String*/res) {
			// summary: return resource
			return this.getConfigResolver().getResource(res) || this.getBaseConfigResolver().getResource(res) || "";
		},

		getResourceIfExists: function(/*String*/res) {
			// summary: return resource if it exists
			return this.getConfigResolver().getResourceIfExists(res) || this.getBaseConfigResolver().getResourceIfExists(res);
		},

		getTypeAction: function(/*String*/type, /*String*/idAction) {
			// summary: convert action resource to displayable string
			return this.getConfigResolver().getTypeAction(type, idAction) || this.getBaseConfigResolver().getTypeAction(type, idAction);
		},

		getTypeAttribute: function(/*String*/type, /*String*/id) {
			// summary: return name of given ConfigurableAttribute; first try resolving using LocaleConfiguration
			// for current locale - if not configured, then use cached resources
			return this.getConfigResolver().getTypeAttribute(type, id) || this.getBaseConfigResolver().getTypeAttribute(type, id);
		},

		getTypeAttributeResource: function(/*String*/type, /*String*/id, /*String*/suffix) {
			// summary: return name of given ConfigurableAttribute; first try resolving using LocaleConfiguration
			// for current locale - if not configured, then use cached resources
			return this.getConfigResolver().getTypeAttributeResource(type, id, suffix) || this.getBaseConfigResolver().getTypeAttributeResource(type, id, suffix) || "";
		},

		getTypeName: function(/*String*/type) {
			// summary: return name of type
			return this.getConfigResolver().getTypeName(type) || this.getBaseConfigResolver().getTypeName(type);
		},

		getTypeResource: function(/*String*/type, /*String*/idRes) {
			// summary: return resource stored with type
			return this.getConfigResolver().getTypeResource(type, idRes) || this.getBaseConfigResolver().getTypeResource(type, idRes) || "";
		},

		setResource: function(/*Writer*/w, /*String*/idAttr, /*String[]*/keys, /*String*/value, /*String?*/userLocale, /*Boolean?*/bHoldClientChange) {
			// summary: set resource on given locale - otherwise default to site locale
			userLocale ||= _getSiteLocale();

			var resources = this.getConfigResolver(userLocale).localeResources;
			var key = keys.join(".");

			w.callMethod("SiteAdmin", "UpdateLocaleConfiguration")
				.addParameter("LanguageCode", xml.ATTR_STRING, userLocale)
				.addParameter("Id", xml.ATTR_STRING, idAttr)
				.addParameter("Map", xml.ATTR_STRINGMAP, csi.objectNew(key, value));

			idAttr = idAttr.toLowerCase();
			value = value || null;

			if (bHoldClientChange) {
				_resourceChanges.push({
					locale: userLocale,
					attr: idAttr,
					keys: keys,
					value: value
				});
			} else {
				// this modifies keys array, so be careful to generate key before calling it
				resources.setResource(resources[idAttr], keys, value);
			}
		},

		flushResources: function() {
			// summary: apply changes to client that were previously held
			_.each(_resourceChanges, function(obj) {
				var resources = this.getConfigResolver(obj.locale).localeResources;
				resources.setResource(resources[obj.attr], obj.keys, obj.value);
			}, this);

			_resourceChanges = [];
		}
	};

	// make locale constants available on this object
	// FIX - separate constants into own module
	lang.mixin(_Locale, LocaleResolver.prototype.statics);

	// the following are used specifically for Product Type matrix views of attributes
	locale.registerDisplayNameResolver("BuildInAttribute", function(node) {
		return _Locale.getConfigurableAttributeNodeName(node);
	});

	locale.registerDisplayNameResolver("CustomAttribute", function(node) {
		return _Locale.getConfigurableAttributeNodeName(node);
	});

	return _Locale;
});
